import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Hls from 'hls.js';
import { REMAINING_SECONDS_TO_IDLE_SCREEN, REMAINING_SECONDS_TO_SKIP, VIDEO_MIME_TYPES } from "../../constants";
import { useVisibleActionContext } from "../VisibleAction";
import { PlaybackContext } from "./PlaybackContext";
import playbackReducer, { getPlaybackReducerInitialState } from "./playbackReducer";
import { calculateBufferedRanges } from "./utils";
import { jsx as _jsx } from "react/jsx-runtime";
var PlaybackProvider = function PlaybackProvider(_ref) {
  var children = _ref.children,
    videoId = _ref.videoId,
    nextVideo = _ref.nextVideo,
    sources = _ref.sources,
    isReady = _ref.isReady,
    isPlaying = _ref.isPlaying,
    wasPlaying = _ref.wasPlaying,
    play = _ref.play,
    pause = _ref.pause,
    replay = _ref.replay,
    togglePlay = _ref.togglePlay,
    seek = _ref.seek,
    onSeek = _ref.onSeek,
    resetReadyState = _ref.resetReadyState,
    onReadyProp = _ref.onReady,
    onPlayProp = _ref.onPlay,
    onPauseProp = _ref.onPause,
    onBackwardProp = _ref.onBackward,
    onForwardProp = _ref.onForward,
    disableSeekForward = _ref.disableSeekForward,
    onTimeUpdateProp = _ref.onTimeUpdate,
    onSpeedChange = _ref.onSpeedChange,
    onSkipVideoEnding = _ref.onSkipVideoEnding,
    endOverlay = _ref.endOverlay,
    videoRef = _ref.videoRef,
    defaultSpeed = _ref.defaultSpeed,
    inactivity = _ref.inactivity,
    autoplay = _ref.autoplay;
  var _useVisibleActionCont = useVisibleActionContext(),
    changeCurrentAction = _useVisibleActionCont.changeCurrentAction;
  var stringifiedSources = JSON.stringify(sources);
  var hasNextVideo = !!nextVideo;
  var _useReducer = useReducer(playbackReducer, getPlaybackReducerInitialState({
      defaultSpeed: defaultSpeed,
      inactivity: inactivity
    })),
    _useReducer2 = _slicedToArray(_useReducer, 2),
    _useReducer2$ = _useReducer2[0],
    currentSource = _useReducer2$.currentSource,
    loading = _useReducer2$.loading,
    currentTime = _useReducer2$.currentTime,
    currentProgress = _useReducer2$.currentProgress,
    duration = _useReducer2$.duration,
    currentSpeed = _useReducer2$.currentSpeed,
    isIdle = _useReducer2$.isIdle,
    visibleIdleScreen = _useReducer2$.visibleIdleScreen,
    ignoreInactivity = _useReducer2$.ignoreInactivity,
    visibleSkip = _useReducer2$.visibleSkip,
    skipDismissed = _useReducer2$.skipDismissed,
    dispatch = _useReducer2[1];
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    bufferedRanges = _useState2[0],
    setBufferedRanges = _useState2[1];
  var stringifiedBuffer = JSON.stringify(bufferedRanges);

  // Interval to check buffer
  var bufferInterval = useRef();
  var onLoadStart = useCallback(function () {
    dispatch({
      type: 'CHANGE_LOADING_STATE',
      loading: true
    });
  }, []);
  var onLoadedData = useCallback(function () {
    dispatch({
      type: 'CHANGE_LOADING_STATE',
      loading: false
    });
  }, []);
  var loadSources = useCallback(function () {
    var hlsSource = sources.find(function (s) {
      return s.type === VIDEO_MIME_TYPES.HLS;
    });
    var newSource;
    if (hlsSource && Hls.isSupported()) {
      newSource = _objectSpread(_objectSpread({}, hlsSource), {}, {
        isHls: true
      });
    } else if (videoRef.current) {
      sources.forEach(function (source) {
        if (videoRef.current && videoRef.current.canPlayType(source.type)) {
          var sourceChild = document.createElement('source');
          sourceChild.src = source.src;
          sourceChild.type = source.type;
          videoRef.current.appendChild(sourceChild);
          if (!newSource) {
            newSource = source;
            videoRef.current.src = source.src;
          }
        }
      });
    }
    dispatch({
      type: 'CHANGE_CURRENT_SOURCE',
      newSource: newSource
    });
    // Ignoring the sources array since we need to compare the stringified version.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, stringifiedSources]);
  useEffect(function () {
    resetReadyState();
    // Remove children from the video element
    while ((_videoRef$current = videoRef.current) !== null && _videoRef$current !== void 0 && _videoRef$current.firstChild) {
      var _videoRef$current;
      videoRef.current.removeChild(videoRef.current.firstChild);
    }
    loadSources();
    // Ignoring loadSources because we just want to run this when the sources change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, videoId, stringifiedSources]);
  useEffect(function () {
    if (isReady && autoplay) {
      play();
    }
    // Ignoring play because we just want to run this when the video is ready or the autoplay prop is enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, autoplay]);
  var onReady = useCallback(function (params) {
    onReadyProp(params);
    if (videoRef.current) {
      videoRef.current.playbackRate = currentSpeed.value;
    }
  }, [videoRef, onReadyProp, currentSpeed.value]);
  var onLoadedMetadata = useCallback(function () {
    if (bufferInterval.current) {
      clearInterval(bufferInterval.current);
    }
    bufferInterval.current = window.setInterval(function () {
      var currentVideo = videoRef.current;
      var _calculateBufferedRan = calculateBufferedRanges(currentVideo),
        bufferedTime = _calculateBufferedRan.bufferedTime,
        calculatedBufferedRanges = _calculateBufferedRan.bufferedRanges;

      // Update state only if necessary to avoid infinite rerenders
      setBufferedRanges(function (prevState) {
        if (JSON.stringify(calculatedBufferedRanges) !== JSON.stringify(prevState)) {
          return calculatedBufferedRanges;
        }
        return prevState;
      });
      if (currentVideo !== null && currentVideo !== void 0 && currentVideo.duration && Math.trunc(bufferedTime) >= Math.trunc(currentVideo.duration)) {
        clearInterval(bufferInterval.current);
      }
    }, 100);
  }, [videoRef]);
  var changeSpeed = useCallback(function (speed) {
    if (videoRef.current) {
      videoRef.current.playbackRate = speed.value;
      dispatch({
        type: 'CHANGE_SPEED',
        newSpeed: speed
      });
      changeCurrentAction('speed');
      if (onSpeedChange) {
        onSpeedChange(speed.value);
      }
    }
  },
  // Ignoring the onSpeedChange prop, assuming it doesn't change after the first render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [videoRef, changeCurrentAction]);
  var onPlay = useCallback(function () {
    onPlayProp();
    changeCurrentAction('play');
  }, [onPlayProp, changeCurrentAction]);
  var onPause = useCallback(function () {
    onPauseProp();
    if (videoRef.current) {
      var _videoRef$current2 = videoRef.current,
        time = _videoRef$current2.currentTime,
        videoDuration = _videoRef$current2.duration;
      if (videoDuration - time >= 1) {
        changeCurrentAction('pause');
      }
    }
  }, [videoRef, changeCurrentAction, onPauseProp]);
  var onEnded = useCallback(function () {
    if (!endOverlay && nextVideo !== null && nextVideo !== void 0 && nextVideo.play && nextVideo !== null && nextVideo !== void 0 && nextVideo.autoplay) {
      nextVideo.play(currentTime);
    }
  },
  // Ignoring nextVideo and endOverlay objects.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [nextVideo === null || nextVideo === void 0 ? void 0 : nextVideo.autoplay, !!endOverlay, currentTime]);
  var onDurationChange = useCallback(function (event) {
    var _event$target;
    dispatch({
      type: 'CHANGE_DURATION',
      duration: ((_event$target = event.target) === null || _event$target === void 0 ? void 0 : _event$target.duration) || 0
    });
  }, []);
  var onTimeUpdate = useCallback(function (event) {
    var videoElement = event.target;
    var time = (videoElement === null || videoElement === void 0 ? void 0 : videoElement.currentTime) || 0;
    var videoDuration = (videoElement === null || videoElement === void 0 ? void 0 : videoElement.duration) || 0;
    var progress = videoDuration ? time / videoDuration : 0;
    if (videoDuration > 0) {
      if (hasNextVideo) {
        // Show idle screen 3 seconds before the video ends if the user is in idle state and inactivity is not ignored
        if (!ignoreInactivity && isIdle && videoDuration - time <= REMAINING_SECONDS_TO_IDLE_SCREEN) {
          pause();
          dispatch({
            type: 'CHANGE_UI_STATE',
            visibleIdleScreen: true,
            visibleSkip: false
          });
          // Show skip button 15 seconds before the video ends if it was not dismissed before and there is no end overlay
        } else if (videoDuration - time <= REMAINING_SECONDS_TO_SKIP && !endOverlay && !visibleSkip && !skipDismissed) {
          dispatch({
            type: 'CHANGE_UI_STATE',
            visibleSkip: true
          });
          // Hide skip button if the video is seeked before the remaining 15 seconds
        } else if (videoDuration - time > REMAINING_SECONDS_TO_SKIP) {
          dispatch({
            type: 'CHANGE_UI_STATE',
            skipDismissed: false,
            visibleSkip: false
          });
        }
      }
      dispatch({
        type: 'CHANGE_PROGRESS',
        currentTime: time,
        currentProgress: progress
      });
      if (onTimeUpdateProp) {
        onTimeUpdateProp({
          currentTime: time,
          currentProgress: progress,
          duration: videoDuration
        });
      }
    }
  }, [onTimeUpdateProp, ignoreInactivity, isIdle, visibleSkip, skipDismissed, hasNextVideo, endOverlay, pause]);
  var onSeeking = useCallback(function (event) {
    var videoElement = event.target;
    var _ref2 = videoElement || {},
      time = _ref2.currentTime,
      videoDuration = _ref2.duration;
    dispatch({
      type: 'CHANGE_PROGRESS',
      currentTime: time,
      currentProgress: videoDuration ? time / videoDuration : 0
    });
  }, []);
  var onBackward = useCallback(function () {
    if (videoRef.current) {
      var target = videoRef.current.currentTime - 10;
      if (target < 0) {
        target = 0;
      }
      seek(target);
      changeCurrentAction('backward');
      if (onBackwardProp) {
        onBackwardProp(target);
      }
    }
    // Ignoring the onBackwardProp prop, assuming it doesn't change after the first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, changeCurrentAction]);
  var onForward = useCallback(function () {
    if (videoRef.current && !disableSeekForward) {
      var target = videoRef.current.currentTime + 10;
      if (target > videoRef.current.duration) {
        target = videoRef.current.duration;
      }
      seek(target);
      changeCurrentAction('forward');
      if (onForwardProp) {
        onForwardProp(target);
      }
    }
    // Ignoring the onForwardProp prop, assuming it doesn't change after the first render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef, changeCurrentAction, disableSeekForward]);
  var onIdleChange = useCallback(function (value) {
    dispatch({
      type: 'CHANGE_UI_STATE',
      isIdle: value
    });
  }, []);
  var toggleIgnoreInactivity = useCallback(function () {
    dispatch({
      type: 'TOGGLE_IGNORE_INACTIVITY'
    });
  }, []);
  var onSkip = useCallback(function () {
    if (nextVideo !== null && nextVideo !== void 0 && nextVideo.play) {
      pause();
      nextVideo.play(currentTime);
      if (onSkipVideoEnding) {
        onSkipVideoEnding({
          remainingSeconds: duration - currentTime
        });
      }
    }
    // Ignoring the onSkipVideoEnding prop, assuming it doesn't change after the first render.
    // Ignoring nextVideo object since we just need to verify if it exists.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNextVideo, currentTime, duration, pause]);
  var onSkipDismiss = useCallback(function () {
    dispatch({
      type: 'CHANGE_UI_STATE',
      visibleSkip: false,
      skipDismissed: true
    });
  }, []);
  var contextValue = useMemo(function () {
    return {
      videoRef: videoRef,
      videoId: videoId,
      autoplay: autoplay,
      nextVideo: nextVideo,
      onSeek: onSeek,
      play: play,
      pause: pause,
      replay: replay,
      togglePlay: togglePlay,
      seek: seek,
      isReady: isReady,
      isPlaying: isPlaying,
      wasPlaying: wasPlaying,
      isStarted: isReady && (autoplay || isPlaying || currentTime > 0),
      isEnded: isReady && !!duration && !isPlaying && currentTime === duration,
      remainingSeconds: duration - currentTime,
      currentSource: currentSource,
      loading: loading,
      bufferedRanges: bufferedRanges,
      currentTime: currentTime,
      currentProgress: currentProgress,
      duration: duration,
      currentSpeed: currentSpeed,
      isIdle: isIdle,
      visibleIdleScreen: visibleIdleScreen,
      ignoreInactivity: ignoreInactivity,
      visibleSkip: visibleSkip,
      skipDismissed: skipDismissed,
      onLoadedMetadata: onLoadedMetadata,
      changeSpeed: changeSpeed,
      onLoadStart: onLoadStart,
      onLoadedData: onLoadedData,
      onReady: onReady,
      onPlay: onPlay,
      onPause: onPause,
      onEnded: onEnded,
      onDurationChange: onDurationChange,
      onTimeUpdate: onTimeUpdate,
      onSeeking: onSeeking,
      onBackward: onBackward,
      onForward: onForward,
      disableSeekForward: disableSeekForward,
      onIdleChange: onIdleChange,
      toggleIgnoreInactivity: toggleIgnoreInactivity,
      onSkip: onSkip,
      onSkipDismiss: onSkipDismiss
    };
  },
  // Ignoring bufferedRanges array since we're including the stringified version.
  // Ignoring nextVideo and currentSource objects since we're including their dynamic properties.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [videoRef, videoId, autoplay, nextVideo === null || nextVideo === void 0 ? void 0 : nextVideo.title, nextVideo === null || nextVideo === void 0 ? void 0 : nextVideo.autoplay, onSeek, play, pause, replay, togglePlay, seek, isReady, isPlaying, wasPlaying, currentSource === null || currentSource === void 0 ? void 0 : currentSource.src, currentSource === null || currentSource === void 0 ? void 0 : currentSource.type, loading, stringifiedBuffer, currentTime, currentProgress, duration, currentSpeed, isIdle, visibleIdleScreen, ignoreInactivity, visibleSkip, skipDismissed, onLoadedMetadata, changeSpeed, onLoadStart, onLoadedData, onReady, onPlay, onPause, onEnded, onDurationChange, onTimeUpdate, onSeeking, onBackward, onForward, disableSeekForward, onIdleChange, toggleIgnoreInactivity, onSkip, onSkipDismiss]);
  return /*#__PURE__*/_jsx(PlaybackContext.Provider, {
    value: contextValue,
    children: children
  });
};
export default PlaybackProvider;